/**
 * logos-marquee.js — brand-neutral Logos Marquee section (see ADR-0003).
 *
 * A row (or up to three) of logos that auto-scrolls forever. The scroll
 * engine is a direct port of the Reviews Wall "marquee" layout variant
 * (assets/reviews-wall.js): one seamless GSAP wrap-loop per row (row 0 in the
 * configured direction, each subsequent row reversed), hover eases the
 * hovered row's speed toward `hoverSpeed` (never a hard stop), and card
 * opacity is driven two mutually-exclusive ways — on pointer devices CSS
 * `:hover` lifts the hovered logo; on small / touch screens a throttled
 * ticker lifts logos whose centre is inside the viewport.
 *
 * Unlike Reviews Wall (which fetches its cards client-side), the logo slides
 * are rendered server-side from section blocks and split across the rows in
 * Liquid, so with no JS / GSAP or under prefers-reduced-motion the section
 * degrades to a static, swipeable strip of the real logos. JS only *tiles*
 * those base slides — it clones them enough times to cover the viewport plus
 * one wrapped period, so the loop is seamless. This is also what satisfies
 * "too few logos to fill the row": the base set simply repeats behind itself
 * and wraps forever (8 logos in a 10-wide row → the first two reappear at the
 * back and loop).
 *
 * ── THE TOOLTIP ────────────────────────────────────────────────────────────
 * Slides may carry `data-lm-quote` (a press pull-quote, or a publisher's award
 * names). It is deliberately not rendered as a caption; one shared chip per
 * section shows it on hover / focus. Three consequences drive the code below:
 *
 *   1. LISTENERS ARE DELEGATED ON THE HOST. `build()` replaces every track's
 *      children and clones each slide several times, so a listener bound to a
 *      slide would be lost on the next rebuild and duplicated across clones.
 *      Delegation on the host survives both, and is bound ONCE against the
 *      persistent abort signal rather than the per-build one.
 *   2. THE CHIP IS REPOSITIONED ON EVERY FRAME WHILE VISIBLE. Hover does not
 *      stop the row — `hoverSpeed` defaults to 1px/s and a merchant may set it
 *      higher — so the logo keeps drifting under the chip. A position computed
 *      once on enter would slide out of alignment.
 *   3. POINTER OPENING IS GATED ON A HOVER-CAPABLE DEVICE. On touch there is no
 *      hover, and a tap belongs to the slide's link (the article). Focus is
 *      wired unconditionally, so keyboard users reach the quote everywhere.
 *
 * ── THE TOUCH CAPTION ──────────────────────────────────────────────────────
 * Because of (3) the chip is unreachable on touch, so in viewport mode a single
 * line below the strip shows the quote of whichever logo is crossing the
 * viewport centre. It is driven from the ticker that already runs there rather
 * than from a second observer, so the highlighted slide and the caption can
 * never disagree about which logo is current.
 *
 * ITS HEIGHT IS MEASURED AND RESERVED. The quotes span 26 to 185 characters, so
 * a caption sized to its content would resize on every rotation and shift the
 * page under the reader's thumb. On entering viewport mode each distinct quote
 * is written into the element once, unpainted, and the tallest result becomes a
 * `min-height`. Re-measured on rebuild, because the width changes with the
 * viewport and so does the wrapping.
 */
(() => {
  if (customElements.get('logos-marquee')) return;

  class LogosMarquee extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-logos-marquee-config]');
      let raw = {};
      if (configEl) {
        try {
          raw = JSON.parse(configEl.textContent);
        } catch (err) {
          console.warn('[logos-marquee] invalid config JSON', err);
        }
      }
      this.cfg = this.normalizeConfig(raw);

      this.container = this.querySelector('[data-logos-marquee-rows]');
      this.caption = this.querySelector('[data-logos-marquee-caption]');
      this.captionKey = null;
      this.tracks = [...this.querySelectorAll('[data-logos-marquee-track]')];
      if (!this.container || !this.tracks.length) return;

      // Snapshot the server-rendered base slides once, before any cloning, so
      // every (re)build starts from a clean template and never destroys the
      // real logos. Each build clones this snapshot for both the base row and
      // the tiling copies.
      this.baseSlides = this.tracks.map((t) => [...t.children]);

      this.tweens = [];
      this.cards = [];
      this.tickerCb = null;
      this.abort = new AbortController();
      // Per-build listeners (row hover) live on their own controller so a
      // resize-rebuild can drop them cleanly without touching the persistent
      // resize / matchMedia listeners below (tied to the main abort signal).
      this.ctrl = new AbortController();

      // Small / touch screens animate opacity by viewport position; wide
      // pointer devices use CSS :hover. Including `(hover: none)` closes the
      // wide-touchscreen gap (>749px but no hover would otherwise get neither).
      this.viewportMM = window.matchMedia('(max-width: 749px), (hover: none)');
      this.viewportMM.addEventListener('change', () => this.applyViewportMode(), {
        signal: this.abort.signal,
      });

      let resizeTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.build(), 200);
        },
        { passive: true, signal: this.abort.signal }
      );

      this.setupTooltip();

      this.build();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.teardown();
    }

    normalizeConfig(raw) {
      const num = (v, dflt) => (Number.isFinite(+v) ? +v : dflt);
      return {
        rows: Math.min(3, Math.max(1, parseInt(raw.rows, 10) || 1)),
        firstDirection: raw.firstDirection === 'left' ? 'left' : 'right',
        // baseSpeed is the divisor for the tween duration — never 0.
        baseSpeed: Math.max(1, num(raw.baseSpeed, 40)),
        // hoverSpeed may be 0 (merchant's call — pauses on hover).
        hoverSpeed: Math.max(0, num(raw.hoverSpeed, 1)),
        speedTransitionMs: Math.max(0, parseInt(raw.speedTransitionMs, 10) || 600),
      };
    }

    build() {
      const cfg = this.cfg;
      const tracks = this.tracks || [];
      if (!this.container || !tracks.length) return;

      // Always start from a clean slate — resize rebuilds re-enter here.
      this.teardown();

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hasGsap = !!(window.gsap && window.gsap.to && window.gsap.utils);
      const staticMode = reduced || !hasGsap;
      this.container.classList.toggle('logos-marquee--static', staticMode);
      this.container.classList.toggle('logos-marquee--on', !staticMode);

      this.tweens = [];
      this.cards = [];

      tracks.forEach((track, ri) => {
        const snapshot = this.baseSlides[ri] || [];
        if (!snapshot.length) return;

        // Fresh base set of clones (the snapshot originals stay detached as a
        // template so the next rebuild is equally clean).
        const base = snapshot.map((node) => node.cloneNode(true));
        track.replaceChildren(...base);

        if (staticMode) {
          this.cards.push(...base);
          return;
        }

        const rowEl = track.parentElement;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        // The repeat period is the VISUAL tiling distance — from the first
        // base slide's box to where the next repeat begins. Measured from
        // layout box offsets (NOT scrollWidth) so any ink that overflows a
        // slide can't inflate the period and open a seam at each wrap. The
        // fixed slot width (CSS) makes this correct even before images load.
        const firstCard = base[0];
        const lastCard = base[base.length - 1];
        const period = lastCard.offsetLeft - firstCard.offsetLeft + lastCard.offsetWidth + gap;
        if (period <= 0) {
          this.cards.push(...base);
          return;
        }

        // Tile enough copies to always cover the viewport plus one wrapped
        // period, so the seam is never visible — and so a base set smaller
        // than the viewport simply repeats behind itself.
        const viewport = rowEl.clientWidth || period;
        const copies = Math.max(2, Math.ceil((viewport + period) / period) + 1);
        for (let c = 1; c < copies; c++) {
          // Clones are purely visual tiling — hide them from assistive tech so
          // each logo is announced once (from the base set), not per copy.
          base.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            track.appendChild(clone);
          });
        }
        this.cards.push(...track.children);

        // Row 0 uses the configured direction; each subsequent row reverses.
        const movingRight = cfg.firstDirection === 'right' ? ri % 2 === 0 : ri % 2 === 1;
        const gsap = window.gsap;
        // Both directions tile copies rightward; only the start/end of x
        // differ, so the modulo wrap seam is identical and jump-free on load.
        gsap.set(track, { x: movingRight ? -period : 0 });
        const tween = gsap.to(track, {
          x: movingRight ? 0 : -period,
          duration: period / cfg.baseSpeed,
          ease: 'none',
          repeat: -1,
          modifiers: { x: gsap.utils.unitize(gsap.utils.wrap(-period, 0), 'px') },
        });
        this.tweens.push(tween);
        this.attachHoverSlowdown(rowEl, tween);
      });

      if (!staticMode) this.applyViewportMode();
    }

    // Ease the hovered row's speed toward hoverSpeed (via timeScale) and back
    // to full on leave — never a hard cut. overwrite:true stops the ease
    // tweens from stacking on rapid enter/leave.
    attachHoverSlowdown(rowEl, tween) {
      const gsap = window.gsap;
      const cfg = this.cfg;
      const dur = cfg.speedTransitionMs / 1000;
      const slowScale = cfg.baseSpeed > 0 ? cfg.hoverSpeed / cfg.baseSpeed : 1;
      const opts = { signal: this.ctrl.signal };
      rowEl.addEventListener(
        'mouseenter',
        () => gsap.to(tween, { timeScale: slowScale, duration: dur, ease: 'power2.out', overwrite: true }),
        opts
      );
      rowEl.addEventListener(
        'mouseleave',
        () => gsap.to(tween, { timeScale: 1, duration: dur, ease: 'power2.inOut', overwrite: true }),
        opts
      );
    }

    // Small / touch screens: lift a card's opacity while its centre is inside
    // the viewport, drop it as it starts to exit. Reads are batched then
    // writes applied (one layout flush) and throttled to ~10fps — plenty for
    // a slow marquee, and the 0.7↔1 fade timing is owned by CSS.
    applyViewportMode() {
      if (!this.container || !window.gsap || !window.gsap.ticker) return;
      if (this.tickerCb) {
        window.gsap.ticker.remove(this.tickerCb);
        this.tickerCb = null;
      }
      const on = this.viewportMM.matches && this.container.classList.contains('logos-marquee--on');
      this.container.classList.toggle('logos-marquee--viewport', on);
      /* ON THE HOST, because the caption is a sibling of `.logos-marquee__rows`
       * (which is what carries `--viewport`) and so is not reachable from it by a
       * descendant selector. */
      this.classList.toggle('is-touch', on);
      if (!on) {
        for (const card of this.cards) card.classList.remove('is-active');
        this.clearCaption();
        return;
      }
      /* Must run AFTER `is-touch` is set: until then the caption is
       * `display: none`, and a display:none box measures 0. */
      this.reserveCaptionHeight();

      let lastT = 0;
      const cb = (time) => {
        if (time - lastT < 0.1) return;
        lastT = time;
        const vw = window.innerWidth;
        const cards = this.cards;
        const centers = new Array(cards.length);
        for (let i = 0; i < cards.length; i++) {
          const r = cards[i].getBoundingClientRect();
          centers[i] = r.left + r.width / 2;
        }
        let bestQuote = null;
        let bestDist = Infinity;
        const mid = vw / 2;
        for (let i = 0; i < cards.length; i++) {
          cards[i].classList.toggle('is-active', centers[i] >= 0 && centers[i] <= vw);
          /* Nearest the CENTRE, not merely on screen: several slides are visible
           * at once, and "on screen" would make the caption depend on DOM order
           * rather than on what the reader is looking at. */
          const quote = cards[i].getAttribute('data-lm-quote');
          if (!quote) continue;
          const dist = Math.abs(centers[i] - mid);
          if (centers[i] >= 0 && centers[i] <= vw && dist < bestDist) {
            bestDist = dist;
            bestQuote = quote;
          }
        }
        this.setCaption(bestQuote);
      };
      this.tickerCb = cb;
      window.gsap.ticker.add(cb);
    }

    // ── Touch caption ──────────────────────────────────────────────────────
    // Written only on CHANGE. The ticker runs ~10x a second; assigning
    // textContent every tick would restyle and re-layout the block continuously
    // and defeat the transition.
    setCaption(quote) {
      if (!this.caption || quote === this.captionKey) return;
      this.captionKey = quote;
      this.caption.textContent = quote || '';
      this.caption.classList.toggle('is-empty', !quote);
    }

    clearCaption() {
      if (!this.caption) return;
      this.captionKey = null;
      this.caption.textContent = '';
      this.caption.classList.add('is-empty');
    }

    // Reserve the tallest quote's height so rotation never resizes the block.
    reserveCaptionHeight() {
      const cap = this.caption;
      if (!cap) return;
      const quotes = [...new Set((this.cards || []).map((c) => c.getAttribute('data-lm-quote')).filter(Boolean))];
      if (!quotes.length) {
        cap.style.minHeight = '';
        return;
      }
      const restore = cap.textContent;
      cap.classList.add('is-measuring');
      cap.style.minHeight = '0px';
      let tallest = 0;
      for (const q of quotes) {
        cap.textContent = q;
        if (cap.offsetHeight > tallest) tallest = cap.offsetHeight;
      }
      cap.textContent = restore;
      cap.style.minHeight = `${tallest}px`;
      cap.classList.remove('is-measuring');
    }

    // ── Tooltip ────────────────────────────────────────────────────────────
    // One chip per section, delegated from the host. See the file header for
    // why it is neither per-slide nor bound per build.
    setupTooltip() {
      this.tip = this.querySelector('[data-logos-marquee-tip]');
      this.tipText = this.querySelector('[data-logos-marquee-tip-text]');
      if (!this.tip || !this.tipText) return;

      this.tipSlide = null;
      this.tipRaf = 0;
      const opts = { signal: this.abort.signal };

      const slideOf = (event) => {
        const el = event.target instanceof Element ? event.target : null;
        if (!el) return null;
        const slide = el.closest('.logos-marquee__slide');
        if (!slide || !this.contains(slide)) return null;
        return slide.hasAttribute('data-lm-quote') ? slide : null;
      };

      // `pointerover` / `pointerout` rather than mouseenter/leave: they bubble,
      // which is what makes one host-level listener cover every slide and every
      // clone. `pointerType` keeps a touch tap from opening a chip the finger is
      // already covering — the tap is the link's.
      this.addEventListener(
        'pointerover',
        (e) => {
          if (e.pointerType === 'touch') return;
          const slide = slideOf(e);
          if (slide) this.showTip(slide);
        },
        opts
      );
      this.addEventListener(
        'pointerout',
        (e) => {
          if (e.pointerType === 'touch') return;
          const slide = slideOf(e);
          // relatedTarget inside the same slide is a move WITHIN it, not a leave.
          if (!slide) return;
          const to = e.relatedTarget instanceof Element ? e.relatedTarget : null;
          if (to && slide.contains(to)) return;
          this.hideTip(slide);
        },
        opts
      );
      this.addEventListener('focusin', (e) => {
        const slide = slideOf(e);
        if (slide) this.showTip(slide);
      }, opts);
      this.addEventListener('focusout', (e) => {
        const slide = slideOf(e);
        if (slide) this.hideTip(slide);
      }, opts);
    }

    showTip(slide) {
      const quote = slide.getAttribute('data-lm-quote');
      if (!this.tip || !quote) return;
      this.tipSlide = slide;
      this.tipText.textContent = quote;
      this.tip.classList.add('is-visible');
      this.positionTip();
      if (!this.tipRaf) this.trackTip();
    }

    hideTip(slide) {
      if (!this.tip) return;
      // A pointerout from a slide that is no longer the tracked one is stale
      // (enter/leave can interleave); ignoring it stops a fresh chip from being
      // closed by the previous slide's leave event.
      if (slide && this.tipSlide && slide !== this.tipSlide) return;
      this.tipSlide = null;
      this.tip.classList.remove('is-visible');
      if (this.tipRaf) {
        cancelAnimationFrame(this.tipRaf);
        this.tipRaf = 0;
      }
    }

    // Follow the logo while the chip is open — the row is still moving.
    trackTip() {
      const step = () => {
        if (!this.tipSlide) {
          this.tipRaf = 0;
          return;
        }
        this.positionTip();
        this.tipRaf = requestAnimationFrame(step);
      };
      this.tipRaf = requestAnimationFrame(step);
    }

    // Adaptive placement: centred on the logo, clamped into the viewport, and
    // flipped below when there is not enough room above. All arithmetic is done
    // in viewport coordinates and converted to host coordinates at the end,
    // because the chip is absolutely positioned against the host.
    positionTip() {
      const slide = this.tipSlide;
      if (!slide || !this.tip) return;
      const MARGIN = 8;
      const GAP = 10;
      const CARET_INSET = 14;

      const hostRect = this.getBoundingClientRect();
      const sRect = slide.getBoundingClientRect();
      const tipW = this.tip.offsetWidth;
      const tipH = this.tip.offsetHeight;
      const vw = window.innerWidth;

      const centerX = sRect.left + sRect.width / 2;
      const maxLeft = Math.max(MARGIN, vw - tipW - MARGIN);
      const leftVp = Math.min(Math.max(centerX - tipW / 2, MARGIN), maxLeft);

      let topVp = sRect.top - tipH - GAP;
      const below = topVp < MARGIN;
      if (below) topVp = sRect.bottom + GAP;

      this.tip.style.left = `${leftVp - hostRect.left}px`;
      this.tip.style.top = `${topVp - hostRect.top}px`;
      this.tip.classList.toggle('logos-marquee__tip--below', below);

      // The caret stays under the logo even after the chip has been clamped
      // sideways — but never escapes its own corners.
      const caret = Math.min(Math.max(centerX - leftVp, CARET_INSET), Math.max(CARET_INSET, tipW - CARET_INSET));
      this.tip.style.setProperty('--lm-tip-caret', `${caret}px`);
    }

    teardown() {
      const gsap = window.gsap;
      if (this.tweens) {
        for (const tween of this.tweens) {
          if (gsap) gsap.killTweensOf(tween);
          tween.kill();
        }
      }
      this.tweens = [];
      if (this.tickerCb && gsap && gsap.ticker) {
        gsap.ticker.remove(this.tickerCb);
      }
      this.tickerCb = null;
      if (this.ctrl) {
        this.ctrl.abort();
        this.ctrl = new AbortController();
      }
      // Reset every track's GSAP transform so the next build measures from x:0
      // (clearProps drops the inline transform GSAP wrote).
      if (gsap) {
        for (const track of this.tracks || []) gsap.set(track, { clearProps: 'transform' });
      }
      // A chip left open would point at a slide `build()` is about to replace.
      this.hideTip(null);
      this.clearCaption();
      this.cards = [];
    }
  }

  customElements.define('logos-marquee', LogosMarquee);
})();
